import React, { useEffect, useRef } from 'react';
import { Formik } from 'formik';
import { usePaginatedService } from '@aph/common-react';
import queryString from 'query-string';
import dayjs from 'dayjs';
import { defaultPageSize } from 'global.constants';
import { parseArrayUrlParameter } from 'global.helpers';
import {
  ILegislationSearchFormData
} from 'types/form.types';
import {
  IUseSearchService,
  IBillsDigestSearchRequest,
  IBillsDigestSearchResponse
} from 'services';

import { LegislationSearchInner } from 'search-interfaces';
import { setupDateForRequest } from 'helpers/form.helpers';

/**
 * BaseLegislationSearchRequest
 *
 * @description Base search request for searching bills and legislation instruments
 */
const baseLegislationSearchRequest: IBillsDigestSearchRequest = {
  portfolios: [],
  currentPage: 1,
  pageSize: defaultPageSize
};

/**
 * InitialFormValues
 *
 * @description Initial form values for legislation search filter form
 */
const initialFormValues: ILegislationSearchFormData = {
  searchTerms: '',
  portfolios: [],
  period: null,
  dateFrom: null,
  dateTo: null
};

/**
 * IPublicationSearch
 *
 * @description Component properties for the publication search component
 */
interface IPublicationSearch {
  searchService: IUseSearchService;
}

/**
 * PublicationSearch
 *
 * @description
 */
const PublicationSearch: React.FC<IPublicationSearch> = ({ searchService }) => {
  const formRef = useRef<any>();

  const billsDigestSearchData = usePaginatedService<IBillsDigestSearchRequest, IBillsDigestSearchResponse>(
    searchService.searchBillsDigest,
    baseLegislationSearchRequest,
    false
  );

  /**
   * HandleSearchLegislation
   *
   * @description Search handling method for searching legislation and bills
   * @param {IBillsDigestSearchRequest} request Updated search request
   * @returns {void}
   */
  const handleSearchLegislation = (request: IBillsDigestSearchRequest): void => {
    billsDigestSearchData.refreshResults(request);
  };

  // Setup the initial search results we need to first pass the URL params
  useEffect(() => {
    const parsedQueryParameters = queryString.parse(window.location.search);

    // To deal with arrays not serialising correctly with a single value we need to parse it using our custom helper
    // This means parameter=value will become ["value"] instead of "value"
    const portfoliosParsed = parseArrayUrlParameter(parsedQueryParameters, 'portfolios');
    const dateFrom = typeof parsedQueryParameters['dateFrom'] === 'string' ? dayjs(parsedQueryParameters['dateFrom']) : null;
    const dateTo = typeof parsedQueryParameters['dateTo'] === 'string' ? dayjs(parsedQueryParameters['dateTo']) : null;

    const initialSearchRequest: IBillsDigestSearchRequest = {
      ...baseLegislationSearchRequest,
      ...parsedQueryParameters,
      portfolios: portfoliosParsed
    };

    handleSearchLegislation(initialSearchRequest);

    if (formRef.current) {
      formRef.current.setValues({
        ...initialFormValues,
        ...parsedQueryParameters,
        portfolios: portfoliosParsed,
        dateFrom: dateFrom,
        dateTo: dateTo
      });
    }
  }, []);

  /**
   * HandleSubmitSearch
   *
   * @description Form submission handler
   * @param {ILegislationSearchFormData} values Form values
   * @returns {void}
   */
  const handleSubmitSearch = (values: ILegislationSearchFormData): void => {
    const updatedSearchRequest: IBillsDigestSearchRequest = {
      currentPage: 1,
      pageSize: defaultPageSize,
      portfolios: values.portfolios
    };

    if (values.searchTerms) {
      updatedSearchRequest.searchTerms = values.searchTerms;
    }

    updatedSearchRequest.dateFrom = setupDateForRequest(values?.dateFrom);
    updatedSearchRequest.dateTo = setupDateForRequest(values?.dateTo);

    handleSearchLegislation(updatedSearchRequest);
  };

  return (
    <div>
      <Formik<ILegislationSearchFormData>
        initialValues={{
          searchTerms: '',
          portfolios: [],
          period: null,
          dateFrom: null,
          dateTo: null
        }}
        // We don't need to run the submit because we are actually refreshing the results on change of each field
        onSubmit={(values) => handleSubmitSearch(values)}
        onReset={() => {
          window.history.replaceState(null, '', '?');
          billsDigestSearchData.refreshResults(baseLegislationSearchRequest);
        }}
        validateOnChange={false}
        validateOnBlur={false}
        innerRef={formRef}
      >
        <LegislationSearchInner
          searchService={searchService}
          searchData={billsDigestSearchData}
          searchRefreshHandler={handleSearchLegislation}
        />
      </Formik>
    </div>
  );
};

export default PublicationSearch;
